import { ActionReducer, createReducer, on } from '@ngrx/store';
import { BoxxGenericState, getGenericInitialState } from '@boxx/core';
import { GMB_STATUS, IGmbLocation } from '@boxx/gmb-core';
import {
  IOnboardingStateError,
  IOnboardingStateSuccess,
} from '../../core/IStateErrorSuccess';
import {
  BusinessModel,
  OnboardingBusinessConfigGmbStatusDataProps,
  OnboardingBusinessConfigNotificationsDataProps,
  OnboardingBusinessConfigPaymentsDataProps,
  OnboardingBusinessConfigScheduleDataProps,
  OnboardingBusinessConfigServicesDataProps,
  OnboardingBusinessConfigSocialNetworksDataProps,
  OnboardingBusinessConfigWebsiteDataProps,
} from '../../models/BusinessConfig.model';
import * as fromBusinessConfigActions from '../onboarding.actions';

export interface IBusinessAdditionalInfoProps {
  services: {
    data: OnboardingBusinessConfigServicesDataProps;
    completed: boolean;
  };
  schedule: {
    data: OnboardingBusinessConfigScheduleDataProps;
    completed: boolean;
  };
  paymentMethods: {
    data: OnboardingBusinessConfigPaymentsDataProps;
    completed: boolean;
  };
  socialNetworks: {
    data: OnboardingBusinessConfigSocialNetworksDataProps;
    completed: boolean;
  };
  gmbStatus: {
    data: OnboardingBusinessConfigGmbStatusDataProps;
    completed: boolean;
  };
  notifications: {
    data: OnboardingBusinessConfigNotificationsDataProps;
    completed: boolean;
  };
  website: {
    data: OnboardingBusinessConfigWebsiteDataProps;
    completed: boolean;
  };
  moduleConcluded: boolean;
}

export interface IBusinessDataAndGmbStatusState {
  businessState: BoxxGenericState<
    BusinessModel,
    IOnboardingStateSuccess,
    IOnboardingStateError
  >;
  gmbStatusState: BoxxGenericState<
    {
      status: GMB_STATUS;
      data: IGmbLocation;
    },
    IOnboardingStateSuccess,
    IOnboardingStateError
  >;
}

export const initialBusinessDataAndGmbStatusState: IBusinessDataAndGmbStatusState =
  {
    businessState: {
      ...getGenericInitialState(),
      data: BusinessModel.empty(),
    },

    gmbStatusState: {
      ...getGenericInitialState(),
      data: { status: 'NO_GMB', data: null },
    },
  };

export const businessDataReducer: ActionReducer<IBusinessDataAndGmbStatusState> =
  createReducer(
    initialBusinessDataAndGmbStatusState,

    on(
      fromBusinessConfigActions.getBusinessDataBeginAction,
      (state): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: true,
          success: null,
          error: null,
        },
      })
    ),

    on(
      fromBusinessConfigActions.getBusinessDataSuccessAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: payload,
          isLoading: false,
          success: { after: 'GET' },
          error: null,
          fetched: true,
        },
      })
    ),
    on(
      fromBusinessConfigActions.getBusinessDataFailAction,
      (state, { errors }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: false,
          success: null,
          error: { after: 'GET', error: errors },
        },
      })
    ),

    // UPDATE BUSINESS - ACTIONS
    on(
      fromBusinessConfigActions.updateBusinessDataBeginAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: true,
          success: null,
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.updateBusinessDataSuccessAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: payload,
          isLoading: false,
          success: {
            after: 'UPDATE',
            data: { action: 'updateBusinessDataSuccessAction' },
          },
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.updateBusinessDataFailAction,
      (state, { errors }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: false,
          success: null,
          error: { after: 'UPDATE', error: errors },
        },
      })
    ),

    // UPDATE BUSINESS-INFO - ACTIONS
    on(
      fromBusinessConfigActions.updateBusinessInfoBeginAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: true,
          success: null,
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.updateBusinessInfoSuccessAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: payload,
          isLoading: false,
          success: {
            after: 'UPDATE',
            data: { action: 'updateBusinessInfoSuccessAction' },
          },
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.updateBusinessInfoFailAction,
      (state, { errors }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: false,
          success: null,
          error: { after: 'UPDATE', error: errors },
        },
      })
    ),

    // CREATE WEBSITE - ACTIONS
    on(
      fromBusinessConfigActions.createWebsiteSuccessAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: { ...state.businessState.data, ...payload },
          isLoading: false,
          success: { after: 'SAVE' },
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.createWebsiteSuccessAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: { ...state.businessState.data, ...payload },
          isLoading: false,
          success: { after: 'SAVE' },
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.updateWebsiteImageSuccessAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: { 
            ...state.businessState.data, 
            images: {
              logo_image: {
                index: 0, 
                content: payload, 
                uploaded: true
              },
              about_image: (state.businessState.data.images.about_image) ? state.businessState.data.images.about_image : null,
              gallery: (state.businessState.data.images.gallery) ? state.businessState.data.images.gallery : [],
            } 
          },
          // ...state.businessState,
          // data: { 
          //   ...state.businessState.data, 
          //   images: {
          //     logo: {
          //       index: 0, 
          //       content: payload, 
          //       uploaded: true
          //     }
          //   } 
          // },
          isLoading: false,
          success: null,
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.createWebsiteFailAction,
      (state, { errors }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: false,
          success: null,
          error: { after: 'SAVE', error: errors },
        },
      })
    ),

    // DELETE BUSINESS IMAGES - ACTIONS
    on(
      fromBusinessConfigActions.deleteBusinessImageBeginAction,
      (state): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: true,
          success: null,
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.deleteBusinessImageSuccessAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          // data: {
          //   ...state.businessState.data,
          //   images: payload,
          // },
          isLoading: false,
          success: { after: 'DELETE_IMAGE'},
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.deleteBusinessImageFailAction,
      (state, { errors }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: true,
          success: null,
          error: { after: 'DELETE_IMAGE', error: errors },
        },
      })
    ),


    on(
      fromBusinessConfigActions.removeLogoSite,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: {
            ...state.businessState.data,
            images: {
              logo_image: null,
              gallery: state.businessState.data.images.gallery,
              about_image: state.businessState.data.images.about_image,
              feature_image: state.businessState.data.images.feature_image,
              contact_image: state.businessState.data.images.contact_image,
              spotlight_image: state.businessState.data.images.spotlight_image,
            },
          },
          isLoading: false,
          success: { after: 'REMOVE_LOGO'},
          error: null,
        },
      })
    ),

    on(
      fromBusinessConfigActions.removeBannerSite,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: {
            ...state.businessState.data,
            images: {
              logo_image: state.businessState.data.images.logo_image,
              gallery: state.businessState.data.images.gallery,
              about_image: null,
              feature_image: state.businessState.data.images.feature_image,
              contact_image: state.businessState.data.images.contact_image,
              spotlight_image: state.businessState.data.images.spotlight_image,
            },
          },
          isLoading: false,
          success: { after: 'REMOVE_BANNER'},
          error: null,
        },
      })
    ),

    on(
      fromBusinessConfigActions.removeMainImageSite,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: {
            ...state.businessState.data,
            images: {
              logo_image: state.businessState.data.images.logo_image,
              gallery: state.businessState.data.images.gallery,
              about_image: state.businessState.data.images.about_image,
              feature_image: null,
              contact_image: state.businessState.data.images.contact_image,
              spotlight_image: state.businessState.data.images.spotlight_image,
            },
          },
          isLoading: false,
          success: { after: 'REMOVE_BANNER'},
          error: null,
        },
      })
    ),

    on(
      fromBusinessConfigActions.removeContactImageSite,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: {
            ...state.businessState.data,
            images: {
              logo_image: state.businessState.data.images.logo_image,
              gallery: state.businessState.data.images.gallery,
              about_image: state.businessState.data.images.about_image,
              feature_image: state.businessState.data.images.feature_image,
              contact_image: null,
              spotlight_image: state.businessState.data.images.spotlight_image,
            },
          },
          isLoading: false,
          success: { after: 'REMOVE_BANNER'},
          error: null,
        },
      })
    ),

    on(
      fromBusinessConfigActions.removeSpotlightImageSite,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: {
            ...state.businessState.data,
            images: {
              logo_image: state.businessState.data.images.logo_image,
              gallery: state.businessState.data.images.gallery,
              about_image: state.businessState.data.images.about_image,
              feature_image: state.businessState.data.images.feature_image,
              contact_image: state.businessState.data.images.contact_image,
              spotlight_image: null,
            },
          },
          isLoading: false,
          success: { after: 'REMOVE_BANNER'},
          error: null,
        },
      })
    ),

    on(
      fromBusinessConfigActions.removeGalleryImageSite,
      (state, { payload }): any => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: {
            ...state.businessState.data,
            images: {
              logo_image: state.businessState.data.images.logo_image,
              gallery: payload,
              about_image: state.businessState.data.images.about_image
            },
          },
          isLoading: false,
          success: { after: 'REMOVE_GALLERY'},
          error: null,
        },
      })
    ),
    

    on(
      fromBusinessConfigActions.updateFieldsSite,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: {
            ...state.businessState.data,
            siteAttributes:payload
          },
          isLoading: false,
          success: { after: 'REFRESH_SITE_FIELDS'},
          error: null,
        },
      })
    ),
    
    on(
      fromBusinessConfigActions.updateImagesSite,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: {
            ...state.businessState.data,
            images: payload,
          },
          isLoading: false,
          success: { after: 'REFRESH_IMAGES_SITE'},
          error: null,
        },
      })
    ),

    on(
      fromBusinessConfigActions.clearRemoveImageSite,
      (state): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: {
            ...state.businessState.data,
          },
          isLoading: false,
          success: null,
          error: null,
        },
      })
    ),

    // UPDATE METADATA - ACTIONS
    on(
      fromBusinessConfigActions.updateMetaBeginAction,
      (state): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: false,
          success: null,
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.updateMetaSuccessAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          data: { ...state.businessState.data, ...payload },
          isLoading: false,
          success: { after: 'UPDATE_META' },
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.updateMetaFailAction,
      (state, { errors }): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: false,
          success: null,
          error: { after: 'UPDATE_META', error: errors },
        },
      })
    ),

    // GET LOCATION STAUS
    on(
      fromBusinessConfigActions.getLocationStatusBeginAction,
      (state): IBusinessDataAndGmbStatusState => ({
        ...state,
        gmbStatusState: {
          ...state.gmbStatusState,
          isLoading: true,
          success: null,
          error: null,
        },
      })
    ),
    on(
      fromBusinessConfigActions.getLocationStatusSuccessAction,
      (state, { payload }): IBusinessDataAndGmbStatusState => ({
        ...state,
        gmbStatusState: {
          data: payload,
          isLoading: false,
          success: { after: 'GET' },
          error: null,
          fetched: true,
        },
      })
    ),
    on(
      fromBusinessConfigActions.getLocationStatusFailAction,
      (state, { errors }): IBusinessDataAndGmbStatusState => ({
        ...state,
        gmbStatusState: {
          ...state.gmbStatusState,
          isLoading: false,
          success: null,
          error: { after: 'GET', error: errors },
          fetched: true,
        },
      })
    ),

    on(
      fromBusinessConfigActions.clearBusinessConfigSuccessErrorDataAction,
      (state): IBusinessDataAndGmbStatusState => ({
        ...state,
        businessState: {
          ...state.businessState,
          isLoading: false,
          success: null,
          error: null,
        },
      })
    ),

        // UPDATE STORE-INFO - ACTIONS
        on(
          fromBusinessConfigActions.updateStoreInfoBeginAction,
          (state, { payload }): IBusinessDataAndGmbStatusState => ({
            ...state,
            businessState: {
              ...state.businessState,
              isLoading: true,
              success: null,
              error: null,
            },
          })
        ),
        on(
          fromBusinessConfigActions.updateStoreInfoSuccessAction,
          (state, { payload }): IBusinessDataAndGmbStatusState => ({
            ...state,
            businessState: {
              ...state.businessState,
              data: {
                ...state.businessState.data,
                ecwic_sync: true
              },
              isLoading: false,
              success: {
                after: 'UPDATE_STORE_ECWID',
                data: { action: 'updateStoreInfoSuccessAction' },
              },
              error: null,
            },
          })
        ),
        on(
          fromBusinessConfigActions.updateStoreInfoFailAction,
          (state, { errors }): IBusinessDataAndGmbStatusState => ({
            ...state,
            businessState: {
              ...state.businessState,
              isLoading: false,
              success: null,
              error: { after: 'UPDATE', error: errors },
            },
          })
        ),

                // UPDATE SCORE-INFO - ACTIONS
                on(
                  fromBusinessConfigActions.updateScoreInfoBeginAction,
                  (state, { payload }): IBusinessDataAndGmbStatusState => ({
                    ...state,
                    businessState: {
                      ...state.businessState,
                      isLoading: true,
                      success: null,
                      error: null,
                    },
                  })
                ),
                on(
                  fromBusinessConfigActions.updateScoreInfoSuccessAction,
                  (state, { payload }): IBusinessDataAndGmbStatusState => ({
                    ...state,
                    businessState: {
                      ...state.businessState,
                        data: {
                          ...state.businessState.data,
                          score: true,
                          stars: payload.calificacion,
                          review: payload.descripcion,
                        },
                        isLoading: false,
                        success: {
                          after: 'UPDATE_SCORE',
                          data: { action: 'updateScoreInfoSuccessAction', payload },
                        },
                        error: null,
                    },
                  })
                ),
                on(
                  fromBusinessConfigActions.updateScoreInfoFailAction,
                  (state, { errors }): IBusinessDataAndGmbStatusState => ({
                    ...state,
                    businessState: {
                      ...state.businessState,
                      isLoading: false,
                      success: null,
                      error: { after: 'UPDATE', error: errors },
                    },
                  })
                )
  );
